"use client";

import { useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { Toaster } from "sonner";
import { SplashScreen } from "./SplashScreen";
import { useIsEmbedded } from "../hooks/useIsEmbedded";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SplashWrapperProps {
  children: ReactNode;
  /** Path to logo for splash screen. Default: "/logo.png" */
  logoSrc?: string;
  /** An SplashScreen durchgereicht: zeichnet das theme-faehige AppIcon
   *  statt der PNG-Grafik. Ohne sie bleibt alles wie bisher. */
  appId?: string;
  /** Custom event name to listen for data readiness.
   *  Default: "app-data-ready".
   *  Dispatch `window.dispatchEvent(new Event("app-data-ready"))` when your initial data is loaded. */
  readyEvent?: string;
  /** Notbremse: nach so vielen ms verschwindet der Splash auch ohne Bereitmeldung.
   *  Default: 4000. Das ist der Ausnahmefall, nicht der Normalweg — wer hier
   *  landet, hat eine App, die ihre Bereitschaft nicht meldet. */
  timeout?: number;
  /**
   * Verzug vor dem Einblenden in ms. Default: **500**.
   *
   * Meldet die App vorher Bereitschaft, erscheint **gar kein** Splash. Das ist
   * der Normalfall: die Apps melden auf janewaylabs.com nach ~250 ms.
   *
   * WARUM UEBERHAUPT EIN VERZUG UND NICHT EINFACH IMMER ZEIGEN: bis 0.210.2 lag
   * der Splash im SSR-Markup und war damit ab dem ersten Bild da — auch wenn die
   * App 300 ms spaeter fertig war. Kuerzer wurde er dadurch zwar, aber sichtbar
   * blieb er immer, und ein Ladebildschirm fuer etwas, das nicht laedt, macht
   * den Wechsel langsamer, nicht schneller.
   */
  delay?: number;
  /**
   * Mindest-Sichtdauer in ms, **nachdem** er eingeblendet wurde. Default: 400.
   *
   * Greift nur, wenn der Splash ueberhaupt erschienen ist. Ohne sie kann er
   * einen Frame nach dem Einblenden wieder verschwinden — und ein Overlay, das
   * fuer 30 ms aufblitzt, liest sich als Fehler, nicht als Ladeanzeige.
   */
  minDuration?: number;
  /** Toaster position. Default: "bottom-right" */
  toasterPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";
  /**
   * Unterdrueckt den Splash-Screen komplett (kein Overlay, `children` rendern
   * sofort). Default `false` (bisheriges Verhalten unveraendert). Use-Case:
   * eingebettete Ansichten (z.B. ein Editor im ai-chat-Artefakt-Panel), in denen
   * ein Splash-Overlay in einer schmalen Seitenleiste stoerend waere.
   */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SplashWrapper({
  children,
  logoSrc,
  appId,
  readyEvent = "app-data-ready",
  timeout = 4000,
  delay = 500,
  minDuration = 400,
  toasterPosition = "bottom-right",
  disabled = false,
}: SplashWrapperProps) {

  // Ein Splash gehoert der Huelle, nicht dem eingebetteten Inhalt: im iframe einer
  // anderen Oekosystem-App nie anzeigen, auch ohne explizites `disabled`.
  const embeddedInFrame = useIsEmbedded();
  const isDisabled = disabled || embeddedInFrame;
  // `false` im ersten Render und damit auch im SSR-Markup: eine schnelle App
  // bekommt so nie ein Overlay zu sehen. Ein Hydration-Unterschied entsteht
  // nicht, weil erst ein Timer im Effekt daran ruehrt.
  const [sichtbar, setSichtbar] = useState(false);
  const [splashDone, setSplashDone] = useState(false);
  const [gemeldet, setGemeldet] = useState(false);
  const [darfWeg, setDarfWeg] = useState(false);
  const sichtbarSeit = useRef(0);
  /** Die Bereitmeldung, sofort lesbar — ohne auf einen React-Render zu warten. */
  const gemeldetRef = useRef(false);

  // Bereitmeldung einsammeln — unabhaengig davon, ob je ein Splash erscheint.
  //
  // Sie landet ZUSAETZLICH in einem Ref, und das ist kein Doppel: der Zeitgeber
  // unten muss sie lesen koennen, BEVOR React die Zustandsaenderung verarbeitet
  // hat. Genau dieser Fall tritt auf einer blockierten Seite ein — siehe dort.
  useEffect(() => {
    if (isDisabled) return;
    const melden = () => {
      gemeldetRef.current = true;
      setGemeldet(true);
    };
    window.addEventListener(readyEvent, melden);
    // Notbremse fuer Apps, die gar nichts melden.
    const notbremse = setTimeout(melden, timeout);
    return () => {
      window.removeEventListener(readyEvent, melden);
      clearTimeout(notbremse);
    };
  }, [isDisabled, readyEvent, timeout]);

  // Einblenden — aber nur, wenn bis dahin niemand Bereitschaft gemeldet hat.
  // `gemeldet` steht in den Abhaengigkeiten: meldet die App vorher, laeuft der
  // Effekt neu, raeumt seinen Timer ab und kehrt sofort zurueck. Der Splash
  // erscheint dann nie — das ist der Normalfall, kein Sonderweg.
  //
  // ── UND DIE PRUEFUNG IM ZEITGEBER IST NICHT DIESELBE PRUEFUNG ──────────────
  //
  // Auf einer blockierten Seite reicht der Effekt nicht. Gemessen am 2026-09-03
  // in `janeway-mail-client` mit 1749 Ordnern in der Seitenleiste: 25 lange
  // Aufgaben, 6612 ms blockierter Hauptthread, laengste einzelne 1,4 s.
  //
  //   2649 ms  React hydriert -> dieser Effekt laeuft, Zeitgeber startet
  //   3259 ms  die App ist bedienbar, ein Klick wirkt
  //   5839 ms  `app-data-ready` — die Meldung ist da
  //   5860 ms  DER SPLASH ERSCHEINT und bleibt 1,7-2,3 s stehen
  //
  // Der Zeitgeber war bei ~3149 ms faellig, kam aber erst dran, als der Thread
  // frei wurde — da war die Meldung laengst eingetroffen. Die Abraeumung im
  // Effekt half nicht: sie braucht selbst einen Render, und der stand in
  // derselben Schlange. Ergebnis war ein Ladebildschirm ueber einer FERTIGEN,
  // bedienbaren App, drei Sekunden zu spaet — der Betreiber beschrieb es als
  // „auf einmal kommt noch mal der gesamte App Splash Screen".
  //
  // Deshalb liest der Rueckruf das Ref und nicht den Zustand: es traegt die
  // Meldung in dem Moment, in dem sie eintrifft, nicht erst nach dem naechsten
  // Render. Ein Splash, der NACH der Bereitmeldung erscheint, ist immer falsch.
  useEffect(() => {
    if (isDisabled || gemeldet || sichtbar) return;
    const t = setTimeout(() => {
      if (gemeldetRef.current) return;
      sichtbarSeit.current = Date.now();
      setSichtbar(true);
    }, delay);
    return () => clearTimeout(t);
  }, [isDisabled, gemeldet, sichtbar, delay]);

  // Ausblenden erst, wenn er lange genug stand. Ohne das blitzt er auf, sobald
  // die Bereitmeldung kurz nach dem Verzug eintrifft.
  useEffect(() => {
    if (!sichtbar || !gemeldet) return;
    const rest = Math.max(0, minDuration - (Date.now() - sichtbarSeit.current));
    const t = setTimeout(() => setDarfWeg(true), rest);
    return () => clearTimeout(t);
  }, [sichtbar, gemeldet, minDuration]);

  const handleDone = useCallback(() => setSplashDone(true), []);

  return (
    <>
      {!isDisabled && sichtbar && !splashDone && (
        <SplashScreen onDone={handleDone} dataReady={darfWeg} logoSrc={logoSrc} appId={appId} />
      )}
      {children}
      {/* M-092: `--jw-toast-unten` am Gebrauchsort lesen — eine App hebt die
          Toasts damit ueber eigene Bedienleisten (mail-client: Fuss des
          Verfassen-Fensters). Rueckfall = Sonners Vorgabe (24 px, Handy 16 px).
          M-109: die Live-Region heisst vorgelesen deutsch. */}
      <Toaster
        position={toasterPosition}
        offset={{ bottom: "var(--jw-toast-unten, 24px)" }}
        mobileOffset={{ bottom: "var(--jw-toast-unten, 16px)" }}
        containerAriaLabel="Benachrichtigungen"
        closeButton
        toastOptions={{
          className:
            "!bg-card !text-card-foreground !border-border !rounded-[var(--radius)] !shadow-lg",
          descriptionClassName: "!text-muted-foreground",
          actionButtonStyle: {
            backgroundColor: "var(--color-primary)",
            color: "var(--color-primary-foreground)",
            borderRadius: "var(--radius)",
          },
          cancelButtonStyle: {
            backgroundColor: "var(--color-muted)",
            color: "var(--color-muted-foreground)",
            borderRadius: "var(--radius)",
          },
        }}
      />
    </>
  );
}
